/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  FormState,
  FormStateBuilder,
  whenCreateMode,
} from '@gh/builder-shared';
import { LicenseAgreementType } from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  EnumDictionary,
  getRouteInitData,
  isRouteForCreationMode,
  streamValueAction,
  UserContext,
} from '@gh/common-shared';
import { isMessageBasket } from '@gh/core-messages';
import {
  DialogService,
  FormMode, NotificationService,
  TinymceSettings,
  tmSettingsBuilder,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { of$, throwError$ } from '@gh/rx';
import { catchError$, filter$, map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { LicenseAgreementDetailsBusinessService } from '../../resources/license-agreement-details-business.service';
import { LicenseAgreementDetails } from '../../resources/license-agreements.model';
import { queryQuoteHasErrors } from '../../../../builder-sales/hlp/components/hlp-details/hlp-details-quote-tab/hlp-quote-details.state';
import { FormControl } from '@angular/forms';

@Component({
  selector:    'gh-license-agreement-details',
  template: require('./license-agreement-details.component.html'),
  styles:   [require('./license-agreement-details.component.scss')],
})
export class LicenseAgreementDetailsComponent extends AbstractFormComponent<LicenseAgreementDetails> implements OnInit {
  form: FormState<LicenseAgreementDetails>;
  licenseAgreementTypeDict: EnumDictionary<LicenseAgreementType>;
  tmSettings: TinymceSettings;

  private initialActiveStatus: boolean;

  constructor(private route: ActivatedRoute,
    private router: AppRouter,
    private dialogs: DialogService,
    private waitings: WaitingService,
    private userContext: UserContext,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    private notifications: NotificationService,
    private formStateBuilder: FormStateBuilder,
    private business: LicenseAgreementDetailsBusinessService,
  ) {
    super();
  }

  get pageTitle(): string {
    if (this.form.createMode) {
      return 'lbl_create_license_agreement';
    } else {
      return 'lbl_edit_license_agreement';
    }
  }

  get completeFlow(): ActionFlow<FormFlowState<LicenseAgreementDetails>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        () => this.form.invalid && this.form.dirty,
        () => this.handleErrors(),
      ),
      execute: streamValueAction((state: FormFlowState<LicenseAgreementDetails>) => {
        const { systemRole, isActive } = state.value;
        let activeStatusValidation$: Observable<any>;
        if (!isActive && this.initialActiveStatus) {
          activeStatusValidation$ = this.dialogs
            .confirmation(yesNoConfirmation(
              'msg_agreement_set_not_relevant',
              { type: this.licenseAgreementTypeDict.get(systemRole) },
            )).pipe(
              filter$(yesAnswer));
        } else {
          activeStatusValidation$ = of$(true);
        }

        if (!this.form.dirty) {
          return activeStatusValidation$;
        }
        if (this.form.invalid) {
          this.handleErrors();
          return activeStatusValidation$;
        }
        return activeStatusValidation$.pipe(
          switchMap$(() => this.business.save(state.value).pipe((
            this.waitings.operator()
          ))),
          catchError$((error) =>
            isMessageBasket(error) && error.has('msg_agreement_change_relevance')
              ? this.dialogs
                .confirmation(yesNoConfirmation(
                  'msg_agreement_change_relevance',
                  { type: this.licenseAgreementTypeDict.get(systemRole) },
                )).pipe(
                  filter$(yesAnswer),
                  switchMap$(() => this.business.save({
                    ...state.value,
                    confirmations: { skipRelevanceCheck: true },
                  }).pipe((
                    this.waitings.operator()
                  ))),
                )
              : throwError$(error)),
          map$((value) => (
            { ...state, value }
          )),
        );
      }),
      finish:  ({ value }) => {
        if (this.form.createMode) {
          this.router.switchTo(['/setup/license-agreements', value.id]);
        } else {
          this.form.setValue(value);
          this.initialActiveStatus = value.isActive;
        }
      },
    });
  }

  ngOnInit(): void {
    const { details, licenseAgreementTypeDict } = getRouteInitData(this.route);

    this.licenseAgreementTypeDict = licenseAgreementTypeDict;

    const mode = isRouteForCreationMode(this.route) ? FormMode.CreateMode : FormMode.EditMode;

    this.initialActiveStatus = details.isActive;

    this.form = this.formStateBuilder.buildPlain({
      mode,
      value:          details,
      optimisticLock: true,
      children:       {
        id:          void 0,
        name:        { required: true },
        description: void 0,
        systemRole:  { required: true, enabled: whenCreateMode() },
        isActive:    void 0,
        eula:        { required: true },
      },
      confirmations:  {
        skipRelevanceCheck: false,
      },
    });
    const builder = tmSettingsBuilder()
      .scayt()
      .height('100%')
      .css('https://fonts.googleapis.com/css?family=Roboto')
      .styles(`
        .mce-content-body { font-family: 'Roboto'; font-size: 15px; padding: 0 80px; width: 360px; }
      `)
      .noFontFamilySelector()
      .fontSizes('8px 10px 12px 14px 15px 18px 24px 36px')
      .build();

    this.tmSettings = {
      ...builder,
      // @ts-ignore
      onChange: (event: any) => {
        this.onDescriptionChange(event);
      },
      onMouseleave: (editor: any) => {
        this.onDescriptionChange({ editor });
      },
    };
  }
  onProcess(): any {
    return this.processFlow.run({
      trigger: 'process',
      form:    this.form,
      value:   this.form.submitValue,
    });
  }
  onDescriptionChange(event: any): void {
    const text = event.editor.getContent({ format: 'text' });
    if (!text || !text.length || text === '\n') {
      this.form.getControl('eula').setValue('');
    }
  }
  private handleErrors(): void {
    for (const field in this.form.group.controls) {

      if (this.form.group.controls.hasOwnProperty(field)
        && this.form.group.controls[field].errors) {
        // @ts-ignore
        this.processNotification(this.form.getControl(field));
        return;
      }
    }
  }

  private processNotification(control: FormControl): void {
    control.markAsTouched();
    if (control.hasError('required')) {
      this.notifications.error('msg_field_required');
      return;
    }
    const errors = control.errors;
    for (const key in errors) {
      if (errors.hasOwnProperty(key)) {
        if (errors[key] && errors[key].messageId) {
          const parameters = {};
          for (const param in errors[key]) {
            if (errors[key].hasOwnProperty(param) && param !== 'messageId') {
              parameters[param] = errors[key][param];
            }
          }
          this.notifications.error(errors[key].messageId, parameters);
        }
      }
    }
  }
}
